import { SANITIZATION_CONFIG } from '../constants/sanitization.constants';

export class SanitizationUtils {
    static isDangerous(html: string): boolean {
        if (!html) { return false; }
        const lowerHtml = html.toLowerCase();
        for (const tag of SANITIZATION_CONFIG.DANGEROUS_TAGS) {
            if (lowerHtml.includes(`<${tag}`) || lowerHtml.includes(`</${tag}`)) {
                return true;
            }
        }
        for (const attr of SANITIZATION_CONFIG.DANGEROUS_ATTRIBUTES) {
            const pattern = new RegExp(`\\b${attr}\\s*=`, 'i');
            if (pattern.test(html)) {
                return true;
            }
        }
        for (const scheme of SANITIZATION_CONFIG.DANGEROUS_URL_SCHEMES) {
            if (lowerHtml.includes(scheme)) {
                return true;
            }
        }
        for (const dataUrl of SANITIZATION_CONFIG.DANGEROUS_DATA_URLS) {
            if (lowerHtml.includes(dataUrl)) {
                return true;
            }
        }
        for (const cssValue of SANITIZATION_CONFIG.DANGEROUS_CSS_VALUES) {
            if (lowerHtml.includes(cssValue)) {
                return true;
            }
        }
        if (
            lowerHtml.includes('&lt;script') ||
            lowerHtml.includes('&lt;iframe') ||
            lowerHtml.includes('&lt;form') ||
            lowerHtml.includes('&lt;svg') ||
            lowerHtml.includes('&lt;meta') ||
            lowerHtml.includes('&lt;base')
        ) {
            return true;
        }
        if (
            lowerHtml.includes('position:fixed') ||
            lowerHtml.includes('position:absolute') ||
            (lowerHtml.includes('width:100%') && lowerHtml.includes('height:100%'))
        ) {
            return true;
        }
        if (lowerHtml.includes('http-equiv') && lowerHtml.includes('refresh')) {
            return true;
        }
        if (lowerHtml.includes('<base') && lowerHtml.includes('href')) {
            return true;
        }
        return false;
    }

    static decodeHtmlEntities(html: string): string {
        if (!html) { return html; }
        const textarea = document.createElement('textarea');
        textarea.innerHTML = html;
        return textarea.value;
    }

    static buildDangerousTagPatterns(): RegExp[] {
        const patterns: RegExp[] = [];
        SANITIZATION_CONFIG.DANGEROUS_TAGS.forEach(tag => {
            patterns.push(new RegExp(`<\\s*${tag}\\b[^>]*>`, 'gi'));
            patterns.push(new RegExp(`<\\s*/${tag}\\s*>`, 'gi'));
            patterns.push(new RegExp(`<\\s*${tag}\\b[^>]*/>`, 'gi'));
        });
        return patterns;
    }

    static buildDangerousTextPatterns(): RegExp[] {
        const patterns: RegExp[] = [];
        patterns.push(/&lt;script\b[^&]*&gt;.*?&lt;\/script&gt;/gi);
        patterns.push(/&lt;iframe\b[^&]*&gt;.*?&lt;\/iframe&gt;/gi);
        patterns.push(/&lt;form\b[^&]*&gt;.*?&lt;\/form&gt;/gi);
        patterns.push(/&lt;svg\b[^&]*&gt;.*?&lt;\/svg&gt;/gi);
        patterns.push(/&lt;object\b[^&]*&gt;.*?&lt;\/object&gt;/gi);
        patterns.push(/&lt;embed\b[^&]*&gt;.*?&lt;\/embed&gt;/gi);
        return patterns;
    }

    static removeDangerousAttributes(html: string): string {
        if (!html) { return html; }
        let sanitized = html;
        SANITIZATION_CONFIG.DANGEROUS_ATTRIBUTES.forEach(attr => {
            const pattern = new RegExp(`\\s+${attr}\\s*=\\s*["'][^"']*["']`, 'gi');
            sanitized = sanitized.replace(pattern, '');
            const pattern2 = new RegExp(`\\s+${attr}\\s*=\\s*[^\\s>]+`, 'gi');
            sanitized = sanitized.replace(pattern2, '');
        });
        sanitized = sanitized.replace(/href\s*=\s*["']javascript:[^"']*["']/gi, '');
        sanitized = sanitized.replace(/href\s*=\s*["']data:(?!image\/)[^"']*["']/gi, '');
        sanitized = sanitized.replace(/href\s*=\s*["']vbscript:[^"']*["']/gi, '');
        sanitized = sanitized.replace(/href\s*=\s*["']https?:\/\/(?!([^\/]+\.)?(?:your-domain\.com|localhost))[^"']*["']/gi, '');
        sanitized = sanitized.replace(/src\s*=\s*["']javascript:[^"']*["']/gi, '');
        sanitized = sanitized.replace(/src\s*=\s*["']vbscript:[^"']*["']/gi, '');
        SANITIZATION_CONFIG.DANGEROUS_DATA_URLS.forEach(dangerousData => {
            const pattern = new RegExp(`src\\s*=\\s*["']${dangerousData.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^"']*["']`, 'gi');
            sanitized = sanitized.replace(pattern, '');
        });
        sanitized = sanitized.replace(/action\s*=\s*["']https?:\/\/(?!([^\/]+\.)?(?:your-domain\.com|localhost))[^"']*["']/gi, '');
        sanitized = sanitized.replace(/action\s*=\s*["']\/\/[^"']*["']/gi, '');
        sanitized = sanitized.replace(/http-equiv\s*=\s*["'][^"']*["']/gi, '');
        sanitized = sanitized.replace(/content\s*=\s*["'][^"']*["']/gi, '');
        return sanitized;
    }

    static sanitizeStyle(style: string): string {
        if (!style) { return ''; }
        let sanitized = style;
        SANITIZATION_CONFIG.DANGEROUS_CSS_VALUES.forEach(dangerousValue => {
            const pattern = new RegExp(dangerousValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
            sanitized = sanitized.replace(pattern, '');
        });
        const allowedProperties = SANITIZATION_CONFIG.ALLOWED_CSS_PROPERTIES;
        const cssRules = sanitized.split(';').map(rule => rule.trim()).filter(rule => rule);
        const safeCssRules = cssRules.filter(rule => {
            const [property] = rule.split(':').map(part => part.trim());
            if (!property) { return false; }
            const lowerProperty = property.toLowerCase();
            if (SANITIZATION_CONFIG.DANGEROUS_CSS_PROPERTIES.includes(lowerProperty)) {
                return false;
            }
            if (allowedProperties.some(allowed =>
                lowerProperty === allowed || lowerProperty.startsWith(allowed + '-'))) {
                const value = rule.split(':').slice(1).join(':').trim().toLowerCase();
                if (value.includes('url(javascript:') || value.includes('url(vbscript:') ||
                    value.includes('expression(')) {
                    return false;
                }
                if (value.includes('url(data:') && !lowerProperty.includes('background')) {
                    return false;
                }
                return true;
            }
            return false;
        });
        return safeCssRules.join('; ');
    }

    static filterAllowedTags(html: string): string {
        if (!html) { return html; }
        const tagRegex = /<(\/?)([\w]+)([^>]*)>/gi;
        return html.replace(tagRegex, (match, closing, tagName, attributes) => {
            const lowerTagName = tagName.toLowerCase();
            if (!SANITIZATION_CONFIG.ALLOWED_TAGS.includes(lowerTagName)) {
                return '';
            }
            if (closing === '/') {
                return `</${lowerTagName}>`;
            }
            let cleanAttributes = '';
            if (SANITIZATION_CONFIG.ALLOWED_STYLE_TAGS.includes(lowerTagName) && attributes.includes('style=')) {
                const styleMatch = attributes.match(/style\s*=\s*["']([^"']*)["']/i);
                if (styleMatch) {
                    const sanitizedStyle = this.sanitizeStyle(styleMatch[1]);
                    if (sanitizedStyle) {
                        cleanAttributes = ` style="${sanitizedStyle}"`;
                    }
                }
            }
            return `<${lowerTagName}${cleanAttributes}>`;
        });
    }

    static sanitizeHtml(html: string): string {
        if (!html) { return ''; }
        let sanitized = html;
        const encodedDangerousPatterns = [
            /&(lt|LT);script\b[^&]*&(gt|GT);.*?&(lt|LT);\/script&(gt|GT);/gi,
            /&(lt|LT);iframe\b[^&]*&(gt|GT);.*?&(lt|LT);\/iframe&(gt|GT);/gi,
            /&(lt|LT);form\b[^&]*&(gt|GT);.*?&(lt|LT);\/form&(gt|GT);/gi,
            /&(lt|LT);svg\b[^&]*&(gt|GT);.*?&(lt|LT);\/svg&(gt|GT);/gi,
            /&(lt|LT);meta\b[^&]*&(gt|GT);/gi,
            /&(lt|LT);base\b[^&]*&(gt|GT);/gi,
            /&(lt|LT);object\b[^&]*&(gt|GT);.*?&(lt|LT);\/object&(gt|GT);/gi,
            /&(lt|LT);embed\b[^&]*&(gt|GT);.*?&(lt|LT);\/embed&(gt|GT);/gi
        ];
        encodedDangerousPatterns.forEach(pattern => {
            sanitized = sanitized.replace(pattern, SANITIZATION_CONFIG.REPLACEMENT_TEXT);
        });
        sanitized = this.decodeHtmlEntities(sanitized);
        sanitized = sanitized.replace(/<meta\s+http-equiv\s*=\s*["']refresh["'][^>]*>/gi,
            SANITIZATION_CONFIG.REPLACEMENT_TEXT);
        sanitized = sanitized.replace(/<base\s+href\s*=\s*["']https?:\/\/[^"']*["'][^>]*>/gi,
            SANITIZATION_CONFIG.REPLACEMENT_TEXT);
        SANITIZATION_CONFIG.DANGEROUS_DATA_URLS.forEach(dangerousData => {
            const pattern = new RegExp(
                `<img[^>]*src\\s*=\\s*["']${dangerousData.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^"']*["'][^>]*>`,
                'gi');
            sanitized = sanitized.replace(pattern, SANITIZATION_CONFIG.REPLACEMENT_TEXT);
        });
        sanitized = sanitized.replace(
            /style\s*=\s*["'][^"']*position\s*:\s*(fixed|absolute)[^"']*["']/gi,
            match => {
                const styleContent = match.toLowerCase();
                if (
                    (styleContent.includes('width') && styleContent.includes('100%')) ||
                    (styleContent.includes('height') && styleContent.includes('100%')) ||
                    styleContent.includes('top:0') ||
                    styleContent.includes('left:0')
                ) {
                    return '';
                }
                return match;
            }
        );
        const dangerousPatterns = this.buildDangerousTagPatterns();
        dangerousPatterns.forEach(pattern => {
            sanitized = sanitized.replace(pattern, SANITIZATION_CONFIG.REPLACEMENT_TEXT);
        });
        sanitized = this.removeDangerousAttributes(sanitized);
        sanitized = this.filterAllowedTags(sanitized);
        sanitized = sanitized.replace(/<(script|iframe|form|object|embed|svg|meta|base|link|style)[^>]*>/gi, '');
        sanitized = sanitized.replace(/<\/(script|iframe|form|object|embed|svg|meta|base|link|style)>/gi, '');
        return sanitized;
    }
}
